import { useState } from 'react';
import useAsync from '../hooks/useAsync';
import { useLookups } from '../context/LookupsContext';
import { jobsApi, clientsApi, retainersApi, authApi } from '../api';
import he from '../i18n/he';
import { issuerOptions } from '../utils/issuer';
import Modal from './Modal';
import { TextInput, NumberInput, DateInput, Select } from './Form';
import ClientPicker from './ClientPicker';
import { ErrorText } from './Loader';
import { isProject } from './engagement';

// Shared create/edit modal. A `job` prop means edit.
//
// The one decision this form makes is `income_source`, and it is asked EXPLICITLY
// rather than inferred from whether a retainer is picked — because the two are
// independent. A job can sit under a retainer and still bill its own brokerage margin
// (the melaveh malka event with a photographer charged on), so "has a retainer" does
// not answer "does it earn".
//
//   own       the job invoices for itself: it needs a stream, an issuer and a split.
//   retainer  a topic: no income of its own, and it MUST have a retainer to roll its
//             costs up to. The server rejects the pair otherwise (topic_requires_retainer),
//             because a topic with nowhere to roll up breaks the partner balance.
//
// The issuing partner is locked once the job has invoices — changing it would
// retroactively misattribute revenue in the settlement (also enforced on the server).
//
// EVERY JOB SITS UNDER AN ENGAGEMENT — a retainer or a one-off project (migration 067).
// The one-off project used to be a job with nothing above it; it is now an engagement that
// holds jobs, so this form no longer creates a job without one (the server refuses it:
// job_requires_engagement). Jobs are added from the retainer or project screen, which
// presets both. "No retainer" survives only when EDITING a job that was already
// standalone before 067, so saving it does not demand a parent it never had.
export default function JobModal({ job, onClose, onSaved }) {
  const lookups = useLookups();
  // Billing picker: customers only — a prospect must never be attachable to a job
  // (the server enforces this too via the invoiceability backstop).
  const { data } = useAsync(() => Promise.all([
    clientsApi.list('customers'), authApi.partners(), retainersApi.list(),
  ]));
  const isNew = !job || !job.id;
  // The issuer is no longer locked once invoices exist. It was, while it doubled as
  // every invoice's issuer; since migration 044 each invoice stores its own and this is
  // only the default a new recording is seeded with, so editing it moves no money.
  // The income source below stays locked — that is a different rule, about invoices
  // being stranded on a job with no income side, and it still holds.
  // Turning a billing job into a topic would strand its invoices on a job with no
  // income side; the server refuses it, so the picker does not offer it.
  const lockIncomeSource = !isNew && Array.isArray(job.invoices) && job.invoices.length > 0;

  const [form, setForm] = useState({
    title: job?.title || '',
    client_id: job?.client_id || '',
    income_source: job?.income_source || 'own',
    retainer_id: job?.retainer_id || '',
    revenue_stream: job?.revenue_stream || lookups.streamOptions[0]?.value || '',
    issuing_partner_id: job?.issuing_partner_id || '',
    agreed_amount_net: job?.agreed_amount_net ?? '',
    // When the work runs. Shown exactly as stored, including not at all — nothing is
    // prefilled with today: a job's dates are a fact about the work somebody knows, and
    // a guessed one would be indistinguishable from a real one. (The retainer form does
    // prefill its start, because a contract signed today is the common case there; a job
    // is as often entered months into a campaign.)
    start_date: job?.start_date ?? '',
    end_date: job?.end_date ?? '',
  });
  const [error, setError] = useState(null);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const [clients, partners, retainers] = data || [[], [], []];

  // `Select` renders no placeholder option, so a `''` state value shows the FIRST
  // entry while the state stays empty — displayed and selected disagree. Derive the
  // effective ids so they cannot: the retainer list filters on the client id, and an
  // empty string quietly matched no retainer at all, leaving a topic with nothing to
  // attach to and the save rejected as topic_requires_retainer.
  const clientId = form.client_id || (clients[0] ? String(clients[0].id) : '');
  // The issuer needs no such fallback: its first option IS the empty value, "טרם הוחלט"
  // (074), so what is shown is what is sent.

  // Only retainers of the selected client: the composite FK pins a job and its
  // retainer to the same client, so anything else would be rejected on save.
  const clientRetainers = retainers.filter((r) => String(r.client_id) === String(clientId));
  const legacyStandalone = !isNew && !job.retainer_id;
  const retainerOptions = [
    ...(legacyStandalone ? [{ value: '', label: he.jobs.noRetainer }] : []),
    ...clientRetainers.map((r) => ({ value: r.id, label: r.title })),
  ];

  // Changing client resets the retainer, which is client-scoped.
  const onClientChange = (id) =>
    setForm((f) => ({ ...f, client_id: id, retainer_id: '' }));

  const isTopic = form.income_source === 'retainer';
  // A topic with no retainer has nothing to roll its costs up to — the server refuses
  // it, so block it here and say why rather than letting the save fail.
  // Shown and sent must agree (see the client id above): with no "none" option, an empty
  // state displays the first engagement, so that is the one this form means.
  const retainerId = form.retainer_id
    || (!legacyStandalone && clientRetainers[0] ? String(clientRetainers[0].id) : '');
  const missingRetainer = !retainerId && (isTopic || !legacyStandalone);
  // The "included" choice is named by the engagement the job sits under (067): inside a
  // one-off project its income is the project's; inside a retainer, the retainer's. Extra
  // income on the engagement itself is an extra charge there, not a job's own income.
  const parent = retainers.find((r) => String(r.id) === String(retainerId));
  let inEngagementLabel = he.jobs.incomeRetainer;
  if (parent) inEngagementLabel = isProject(parent) ? he.jobs.incomeInProject : he.jobs.incomeInRetainer;

  const submit = async () => {
    try {
      const base = {
        title: form.title,
        client_id: Number(clientId) || null,
        income_source: form.income_source,
        retainer_id: Number(retainerId) || null,
        // A topic carries none of the income-describing fields — it has no income to
        // classify, invoice or agree a value for.
        revenue_stream: isTopic ? null : (form.revenue_stream || lookups.streamOptions[0]?.value),
        issuing_partner_id: isTopic ? null : Number(form.issuing_partner_id) || null,
        agreed_amount_net: !isTopic && form.agreed_amount_net !== '' ? Number(form.agreed_amount_net) : null,
        // Sent on BOTH kinds of job, and outside the isTopic split above: the dates
        // describe the work, and a topic is work. They are the one thing on this form
        // the income source has nothing to say about.
        start_date: form.start_date || null,
        end_date: form.end_date || null,
      };
      if (isNew) {
        await jobsApi.create(base);
      } else {
        // No `status` here. It used to be echoed back on every save purely so the PUT
        // would not write a NULL into a NOT NULL column, which quietly made an edit of
        // the title the request that also decided the job's status. The server now falls
        // back to the stored value, and finishing work is its own act (JobStatusModal).
        await jobsApi.update(job.id, base);
      }
      onSaved();
    } catch (e) {
      setError(e);
    }
  };

  return (
    <Modal
      title={isNew ? he.jobs.newJob : he.jobs.editJob}
      onClose={onClose}
      onSave={submit}
      saveDisabled={!form.title || !clientId || missingRetainer}
    >
      <TextInput label={he.jobs.jobTitle} value={form.title} onChange={set('title')} />
      <ClientPicker label={he.jobs.client} value={clientId} onChange={onClientChange} clients={clients} />
      {/* The two dates sit with the title and the client — what the work IS — rather
          than among the income fields below, which disappear on a topic. Neither is
          required, and no rule anywhere compares them with the retainer's term: a job
          that runs past its engagement is marked on the screens and refused by nothing.
          Save is still blocked on a HALF-TYPED date, which is a different thing — that
          guard belongs to DateInput and applies to every date in the app. */}
      <DateInput label={he.jobs.workStartDate} value={form.start_date} onChange={set('start_date')} />
      <DateInput label={he.jobs.workEndDate} value={form.end_date} onChange={set('end_date')} />
      <Select
        label={he.jobs.incomeSource}
        value={form.income_source}
        onChange={set('income_source')}
        options={[
          { value: 'own', label: he.jobs.incomeOwn },
          { value: 'retainer', label: inEngagementLabel },
        ]}
        disabled={lockIncomeSource}
      />
      <Select label={he.jobs.engagement} value={retainerId} onChange={set('retainer_id')} options={retainerOptions} />
      {missingRetainer && (
        <p className="field-error">
          {clientRetainers.length === 0 ? he.jobs.noRetainerForClient : he.jobs.pickRetainerForTopic}
        </p>
      )}
      {!isTopic && (
        <>
          <Select label={he.jobs.stream} value={form.revenue_stream} onChange={set('revenue_stream')} options={lookups.streamOptions} />
          <Select
            label={he.jobs.defaultIssuer}
            value={form.issuing_partner_id}
            onChange={set('issuing_partner_id')}
            options={issuerOptions(partners)}
          />
          <NumberInput label={he.jobs.agreedAmount} value={form.agreed_amount_net} onChange={set('agreed_amount_net')} />
        </>
      )}
      {error && <ErrorText error={error} />}
    </Modal>
  );
}
